// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

// ========================================================================
// Wizard Step Definitions
// ========================================================================

import type { ProviderSubmissionResult } from './providerAccess';

/** All wizard step IDs */
export type WizardStepId =
  | 'loa-validation'
  | 'provider-details'
  | 'policyholder-details'
  | 'policy-details'
  | 'employment-details'
  | 'contribution-details'
  | 'transfers-in'
  | 'crystallised-retired'
  | 'uploads'
  | 'investments'
  | 'charges-fees'
  | 'other-charges-details'
  | 'other-information'
  | 'transaction-history'
  | 'associated-benefits';

/** Pension wizard step order */
export const PENSION_STEP_ORDER: readonly WizardStepId[] = [
  'loa-validation',           // Step 0: Gate
  'provider-details',         // Step 1
  'policyholder-details',     // Step 2
  'policy-details',           // Step 3 (pivotal — drives visibility)
  'employment-details',       // Step 4 (conditional)
  'contribution-details',     // Step 5 (conditional)
  'transfers-in',             // Step 6 (conditional)
  'crystallised-retired',     // Step 7 (conditional: ARF & Vested PRSA only)
  'investments',              // Step 8
  'charges-fees',             // Step 9
  'other-information',        // Step 10
  'transaction-history',      // Step 11
  'associated-benefits',      // Step 12
] as const;

/** Investment wizard step order */
export const INVESTMENT_STEP_ORDER: readonly WizardStepId[] = [
  'loa-validation',
  'provider-details',
  'policyholder-details',
  'policy-details',
  'uploads',
  'investments',
  'charges-fees',
  'other-charges-details',
  'other-information',
] as const;

/** All wizard step IDs as an ordered array (superset — use getActiveSteps for dynamic filtering) */
export const WIZARD_STEP_ORDER: readonly WizardStepId[] = [
  'loa-validation',
  'provider-details',
  'policyholder-details',
  'policy-details',
  'employment-details',
  'contribution-details',
  'transfers-in',
  'crystallised-retired',
  'uploads',
  'investments',
  'charges-fees',
  'other-charges-details',
  'other-information',
  'transaction-history',
  'associated-benefits',
] as const;

/** Human-readable step names for sidebar display */
export const WIZARD_STEP_NAMES: Record<WizardStepId, string> = {
  'loa-validation': 'Letter of Authority',
  'provider-details': 'Provider details',
  'policyholder-details': 'Policy holder details',
  'policy-details': 'Policy details',
  'employment-details': 'Employment details',
  'contribution-details': 'Contribution details',
  'transfers-in': 'Transfers in',
  'crystallised-retired': 'Crystallised / Retired',
  'uploads': 'Uploads',
  'investments': 'Investment funds',
  'charges-fees': 'Charges & fees',
  'other-charges-details': 'Other charges and details',
  'other-information': 'Other information',
  'transaction-history': 'Transaction history',
  'associated-benefits': 'Other associated benefits',
};

// ========================================================================
// Policy Types (Irish pension/investment products)
// ========================================================================

/** Policy type keys matching the CSV specification */
export type PolicyTypeKey =
  | 'PRB_BOB'
  | 'PRSA_STANDARD'
  | 'PRSA_NON_STANDARD'
  | 'PRSA_AVC'
  | 'PERSONAL_PENSION'
  | 'DEFINED_CONTRIBUTION'
  | 'EXECUTIVE_PENSION'
  | 'MASTERTRUST'
  | 'DIRECTORS_PENSION'
  | 'ARF'
  | 'VESTED_PRSA'
  | 'INVESTMENT_BOND'
  | 'REGULAR_SAVER'
  | 'CHILDS_SAVER'
  | 'TRADING_ACCOUNTS'
  | 'INVESTMENT_PORTFOLIO';

/** Policy type display labels */
export const POLICY_TYPE_LABELS: Record<PolicyTypeKey, string> = {
  PRB_BOB: 'PRB / Buy-out Bond',
  PRSA_STANDARD: 'PRSA (Standard)',
  PRSA_NON_STANDARD: 'PRSA (Non-Standard)',
  PRSA_AVC: 'PRSA AVC',
  PERSONAL_PENSION: 'Personal Pension',
  DEFINED_CONTRIBUTION: 'Defined Contribution',
  EXECUTIVE_PENSION: 'Executive Pension',
  MASTERTRUST: 'Mastertrust',
  DIRECTORS_PENSION: "Director's Pension",
  ARF: 'ARF (Approved Retirement Fund)',
  VESTED_PRSA: 'Vested PRSA',
  INVESTMENT_BOND: 'Investment Bond',
  REGULAR_SAVER: 'Regular Saver',
  CHILDS_SAVER: "Child's Saver",
  TRADING_ACCOUNTS: 'Trading Accounts',
  INVESTMENT_PORTFOLIO: 'Investment Portfolio',
};

/** Pension policy type keys */
export const PENSION_TYPE_KEYS: readonly PolicyTypeKey[] = [
  'PRB_BOB',
  'PRSA_STANDARD',
  'PRSA_NON_STANDARD',
  'PRSA_AVC',
  'PERSONAL_PENSION',
  'DEFINED_CONTRIBUTION',
  'EXECUTIVE_PENSION',
  'MASTERTRUST',
  'DIRECTORS_PENSION',
  'ARF',
  'VESTED_PRSA',
] as const;

/** Investment policy type keys */
export const INVESTMENT_TYPE_KEYS: readonly PolicyTypeKey[] = [
  'INVESTMENT_BOND',
  'REGULAR_SAVER',
  'CHILDS_SAVER',
  'TRADING_ACCOUNTS',
  'INVESTMENT_PORTFOLIO',
] as const;

/** All policy type keys in display order */
export const POLICY_TYPE_KEYS: readonly PolicyTypeKey[] = [
  ...PENSION_TYPE_KEYS,
  ...INVESTMENT_TYPE_KEYS,
] as const;

/** Check if a policy type key is an investment type */
export function isInvestmentType(key: PolicyTypeKey): boolean {
  return (INVESTMENT_TYPE_KEYS as readonly string[]).includes(key);
}

// ========================================================================
// Policy-Type Visibility Matrix
// ========================================================================

/** Which conditional steps are shown for each policy type */
export interface StepVisibility {
  'employment-details': boolean;
  /** Whether the severance sub-section appears within Employment */
  severanceSubSection: boolean;
  'contribution-details': boolean;
  'transfers-in': boolean;
  'crystallised-retired': boolean;
}

/** Default investment visibility (all pension conditional steps hidden) */
const INVESTMENT_VISIBILITY: StepVisibility = {
  'employment-details': false, severanceSubSection: false, 'contribution-details': false, 'transfers-in': false, 'crystallised-retired': false,
};

export const POLICY_TYPE_VISIBILITY: Record<PolicyTypeKey, StepVisibility> = {
  PRB_BOB:              { 'employment-details': true,  severanceSubSection: true,  'contribution-details': false, 'transfers-in': true,  'crystallised-retired': false },
  PRSA_STANDARD:        { 'employment-details': true,  severanceSubSection: false, 'contribution-details': true,  'transfers-in': true,  'crystallised-retired': false },
  PRSA_NON_STANDARD:    { 'employment-details': true,  severanceSubSection: false, 'contribution-details': true,  'transfers-in': true,  'crystallised-retired': false },
  PRSA_AVC:             { 'employment-details': true,  severanceSubSection: false, 'contribution-details': true,  'transfers-in': true,  'crystallised-retired': false },
  PERSONAL_PENSION:     { 'employment-details': false, severanceSubSection: false, 'contribution-details': true,  'transfers-in': true,  'crystallised-retired': false },
  DEFINED_CONTRIBUTION: { 'employment-details': true,  severanceSubSection: true,  'contribution-details': true,  'transfers-in': true,  'crystallised-retired': false },
  EXECUTIVE_PENSION:    { 'employment-details': true,  severanceSubSection: true,  'contribution-details': true,  'transfers-in': true,  'crystallised-retired': false },
  MASTERTRUST:          { 'employment-details': true,  severanceSubSection: true,  'contribution-details': true,  'transfers-in': true,  'crystallised-retired': false },
  DIRECTORS_PENSION:    { 'employment-details': true,  severanceSubSection: true,  'contribution-details': true,  'transfers-in': true,  'crystallised-retired': false },
  ARF:                  { 'employment-details': false, severanceSubSection: false, 'contribution-details': false, 'transfers-in': false, 'crystallised-retired': true  },
  VESTED_PRSA:          { 'employment-details': false, severanceSubSection: false, 'contribution-details': false, 'transfers-in': false, 'crystallised-retired': true  },
  INVESTMENT_BOND:      INVESTMENT_VISIBILITY,
  REGULAR_SAVER:        INVESTMENT_VISIBILITY,
  CHILDS_SAVER:         INVESTMENT_VISIBILITY,
  TRADING_ACCOUNTS:     INVESTMENT_VISIBILITY,
  INVESTMENT_PORTFOLIO: INVESTMENT_VISIBILITY,
};

/** Pension steps that are always shown regardless of pension policy type */
const PENSION_ALWAYS_SHOWN_STEPS: ReadonlySet<WizardStepId> = new Set<WizardStepId>([
  'loa-validation',
  'provider-details',
  'policyholder-details',
  'policy-details',
  'investments',
  'charges-fees',
  'other-information',
  'transaction-history',
  'associated-benefits',
]);

/** When policy is closed (retired or transferred out), form ends after policyholder details */
const CLOSED_POLICY_STEPS: readonly WizardStepId[] = [
  'loa-validation',
  'provider-details',
  'policyholder-details',
];

/** Conditional step IDs (the ones controlled by the visibility matrix — pension only) */
const CONDITIONAL_STEPS: ReadonlySet<WizardStepId> = new Set<WizardStepId>([
  'employment-details',
  'contribution-details',
  'transfers-in',
  'crystallised-retired',
]);

/**
 * Compute the active step list for a given policy type, status, and category.
 * If policy is closed (retired or transferred out), only the first 3 steps are shown.
 * If no policy type is selected, returns steps based on category (or pension default).
 */
export function getActiveStepsForPolicyType(
  policyType: PolicyTypeKey | null,
  policyStatus?: string | null,
  policyTypeCategory?: string | null,
): WizardStepId[] {
  // When policy is closed, form ends after policyholder details
  if (policyStatus === 'CLOSED') {
    return [...CLOSED_POLICY_STEPS];
  }

  // Determine if this is an investment flow
  const isInvestment = policyTypeCategory?.toUpperCase() === 'INVESTMENT'
    || (policyType != null && isInvestmentType(policyType));

  // Investment flow: return the fixed investment step order (no conditional steps)
  if (isInvestment) {
    return [...INVESTMENT_STEP_ORDER];
  }

  if (!policyType) {
    // Before policy type is selected, show all pension steps
    return [...PENSION_STEP_ORDER];
  }

  const visibility = POLICY_TYPE_VISIBILITY[policyType];
  return PENSION_STEP_ORDER.filter((stepId) => {
    if (PENSION_ALWAYS_SHOWN_STEPS.has(stepId)) return true;
    if (CONDITIONAL_STEPS.has(stepId)) {
      return visibility[stepId as keyof StepVisibility] === true;
    }
    return true;
  });
}

// ========================================================================
// Step completion and validation
// ========================================================================

/** Step completion and validation status */
export type WizardStepStatus = 'incomplete' | 'complete' | 'error';

/** Single step metadata */
export interface WizardStep {
  id: WizardStepId;
  name: string;
  index: number; // 0-based within the active step list
  status: WizardStepStatus;
  savedAt: string | null; // ISO-8601
}

// ========================================================================
// Wizard State (React)
// ========================================================================

/** Wizard navigation and validation state managed by useWizardState */
export interface WizardState {
  currentStepId: WizardStepId;
  currentStepIndex: number;
  steps: WizardStep[];
  canNavigateForward: boolean;
  canNavigateBack: boolean;
  isFirstStep: boolean;
  isLastStep: boolean;
  allStepsComplete: boolean;
}

/** Validation error for a single field */
export interface WizardFieldError {
  field: string;
  message: string;
}

/** Validation result for a step */
export interface StepValidationResult {
  valid: boolean;
  errors: WizardFieldError[];
}

// ========================================================================
// localStorage Persistence
// ========================================================================

/** File metadata stored in localStorage (file content is NOT stored) */
export interface PendingUpload {
  stepId: WizardStepId;
  fieldKey: string;
  fileName: string;
  fileSize: number;
  fileType: string;
}

/** Per-step data in localStorage */
export interface StepStorageData {
  completed: boolean;
  savedAt: string | null; // ISO-8601
  data: Record<string, unknown>;
}

/** Top-level localStorage structure keyed by loa-wizard-{token} */
export interface WizardStorageData {
  version: number;
  lastUpdatedAt: string; // ISO-8601
  lastUpdatedStep: WizardStepId;
  currentStep: WizardStepId;
  steps: Partial<Record<WizardStepId, StepStorageData>>;
  pendingUploads: PendingUpload[];
}

/** Current localStorage schema version — bump when step structure changes */
export const WIZARD_STORAGE_VERSION = 4;

/** localStorage key format */
export const getWizardStorageKey = (token: string): string => `loa-wizard-${token}`;

// ========================================================================
// Step Field Definitions (for validation engine)
// ========================================================================

/** Supported field input types */
export type StepFieldType =
  | 'text'
  | 'email'
  | 'date'
  | 'phone'
  | 'currency'
  | 'dropdown'
  | 'radio'
  | 'checkbox'
  | 'textarea'
  | 'file'
  | 'number';

/** Definition of a single form field within a step */
export interface StepFieldDefinition {
  key: string;
  label: string;
  type: StepFieldType;
  required: boolean;
  maxLength?: number;
  /** For conditional required fields (e.g., file upload when Yes/No is "Yes") */
  requiredWhen?: {
    field: string;
    value: string | boolean;
  };
  /** Dropdown options */
  options?: Array<{ value: string; label: string }>;
  /** Additional validation: must be a past date */
  pastDate?: boolean;
  /** For number fields: minimum value */
  min?: number;
  /** For number fields: maximum value */
  max?: number;
}

// ========================================================================
// Submission Orchestration
// ========================================================================

/** Result of a file upload attempt during the "Complete" flow */
export interface FileUploadResult {
  fileName: string;
  stepId: WizardStepId;
  fieldKey: string;
  success: boolean;
  submissionId?: string;
  error?: string;
}

/** Overall wizard submission result */
export interface WizardSubmissionResult {
  formSubmissionResult: ProviderSubmissionResult | null;
  fileUploadResults: FileUploadResult[];
  allUploadsSucceeded: boolean;
  formSubmissionSucceeded: boolean;
}

// ========================================================================
// Step Component Props (common pattern)
// ========================================================================

/** Common props shared by all step components */
export interface StepComponentProps {
  /** Current step data from localStorage/state */
  data: Record<string, unknown>;
  /** Update handler for field changes */
  onFieldChange: (field: string, value: unknown) => void;
  /** Per-field validation errors */
  fieldErrors: Record<string, string>;
  /** Clear a single field error (on user input) */
  onClearFieldError: (field: string) => void;
  /** For file upload steps: pending files held in memory (multiple per field) */
  pendingFiles?: Map<string, File[]>;
  /** For file upload steps: handler to add a pending file */
  onAddFile?: (fieldKey: string, file: File) => void;
  /** For file upload steps: handler to remove a pending file by index */
  onRemoveFile?: (fieldKey: string, index?: number) => void;
}
